import { PLACEHOLDER_NAME, type UserProfile } from '@calling/shared';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { APP_NAME } from '../lib/platform';
import { HostSteps } from './Education';
import { Dialog } from './ui';

/**
 * The welcome after a new account is made. An account behind Apple's private
 * address says what that address is; one that Apple shared no name for (it
 * shares it only once, and a private address says nothing) is asked for the
 * name people will see in meetings.
 */
export function WelcomeDialog({ user, passwordOffered, emails, onClose }: { user: UserProfile; passwordOffered: boolean; emails: boolean; onClose: () => void }) {
  const nameless = user.name === PLACEHOLDER_NAME;
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError('Enter a name between 1 and 40 characters.');
    setSaving(true);
    try {
      const res = await api.request<{ user: UserProfile }>('/api/me', { method: 'PATCH', body: { name } });
      api.setUser(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your name could not be saved. You can change it later in Settings.');
    }
    setSaving(false);
  };
  return (
    <Dialog title={nameless ? 'Welcome' : `Welcome, ${user.name.split(' ')[0]}`} onClose={onClose} testId="welcome">
      <p>
        {user.password && user.providers.length === 0 ? (
          <>
            Your {APP_NAME} account is ready. You signed up as <b>{user.email}</b>. Next time, sign in with your email address and password.
          </>
        ) : user.privateRelay ? (
          <>
            Your {APP_NAME} account is ready. You signed in with Apple, which gave us a private address for you: <b>{user.email}</b>. Email we send there reaches your own inbox.
            {passwordOffered ? '' : ' There is no separate password to remember.'}
          </>
        ) : passwordOffered ? (
          <>
            Your {APP_NAME} account is ready. You signed in as <b>{user.email}</b>.
          </>
        ) : (
          <>
            Your {APP_NAME} account is ready. You signed in as <b>{user.email}</b>; there is no separate password to remember.
          </>
        )}
        {emails ? ' We’ve emailed you this guide too.' : ''}
      </p>
      {nameless ? (
        <form className="field" onSubmit={save} noValidate data-testid="welcome-name">
          <label htmlFor="welcome-name">{user.providers.includes('apple') ? 'Apple did not share your name.' : 'We don’t know your name yet.'} Type the name people will see in your meetings.</label>
          <div className="row">
            <input
              id="welcome-name"
              className="input"
              maxLength={40}
              autoComplete="name"
              value={name}
              aria-invalid={error ? true : undefined}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
            />
            <button className="btn btn--medium" style={{ height: 50 }} disabled={saving}>
              Save
            </button>
          </div>
          {error ? (
            <span className="error" role="alert">
              {error}
            </span>
          ) : null}
        </form>
      ) : null}
      <HostSteps />
      <button className="btn btn--primary btn--block" onClick={onClose}>
        Got it
      </button>
    </Dialog>
  );
}
